import React, { useState, useEffect } from 'react';
import { useAuth, apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { 
    Shield, Save, RefreshCw, ChevronDown, ChevronRight
} from 'lucide-react';
import MODULE_HIERARCHY from '@/config/moduleRegistry';

const STATIC_ROLES = [
    { id: 'admin', label: 'Admin' },
    { id: 'sales_manager', label: 'Sales Manager' },
    { id: 'team_leader', label: 'Team Leader' },
    { id: 'sales_executive', label: 'Sales Executive' },
    { id: 'cs_head', label: 'CS Head' },
    { id: 'cs_agent', label: 'CS Agent' },
    { id: 'mentor', label: 'Mentor' },
    { id: 'master_of_academics', label: 'Master of Academics' },
    { id: 'business_development', label: 'Business Development' },
    { id: 'business_development_manager', label: 'BD Manager' },
    { id: 'bd_executive', label: 'BD Executive' },
    { id: 'finance', label: 'Finance' },
    { id: 'hr', label: 'HR' },
    { id: 'accounts', label: 'Accounts' },
    { id: 'marketing', label: 'Marketing' },
    { id: 'operations', label: 'Operations' },
    { id: 'quality_control', label: 'Quality Control' },
    { id: 'staff', label: 'Staff' },
];

// MODULE_HIERARCHY is imported from shared config: @/config/moduleRegistry

const PERMISSION_LEVELS = [
    { id: 'none', label: 'None', color: 'bg-gray-200 text-gray-600' },
    { id: 'view', label: 'View', color: 'bg-blue-100 text-blue-700' },
    { id: 'edit', label: 'Edit', color: 'bg-amber-100 text-amber-700' },
    { id: 'full', label: 'Full', color: 'bg-emerald-100 text-emerald-700' },
];

// Get default permissions based on role
const getDefaultPermissions = (role) => {
    const permissions = {};
    
    // Initialize all to none
    MODULE_HIERARCHY.forEach(module => {
        permissions[module.id] = { enabled: false, level: 'none', subPages: {} };
        module.subPages.forEach(subPage => {
            permissions[module.id].subPages[subPage.id] = 'none';
        });
    });
    
    // SSHR is available to all logged-in users by default
    permissions['sshr'] = { enabled: true, level: 'view', subPages: {
        sshr_dashboard: 'view', sshr_payslips: 'view', sshr_calendar: 'view',
        sshr_announcements: 'view', sshr_tasks: 'edit',
    }};
    // Communication is available to all logged-in users
    permissions['communication'] = { enabled: true, level: 'view', subPages: {
        team_chat: 'view', knowledge_base: 'view',
    }};
    // IT Assets available to all
    permissions['it_assets'] = { enabled: true, level: 'view', subPages: {
        it_assets_page: 'view',
    }};
    
    // Set role-specific defaults
    if (role === 'admin') {
        MODULE_HIERARCHY.forEach(module => {
            permissions[module.id] = { enabled: true, level: 'full', subPages: {} };
            module.subPages.forEach(subPage => {
                permissions[module.id].subPages[subPage.id] = 'full';
            });
        });
        // Admin can't access super admin settings
        permissions['settings'].subPages['access_control'] = 'view';
        permissions['settings'].subPages['password_resets'] = 'none';
    } else if (role === 'sales_manager') {
        permissions['dashboard'] = { enabled: true, level: 'view', subPages: { main_dashboard: 'view', qc_dashboard: 'view' } };
        permissions['sales'] = { enabled: true, level: 'full', subPages: { 
            sales_crm: 'full', sales_dashboard: 'full', today_followups: 'full', leads_pool: 'full', approvals: 'full' 
        }};
        permissions['customer_service'] = { enabled: true, level: 'view', subPages: { 
            cs_kanban: 'view', cs_dashboard: 'view', customer_master: 'view' 
        }};
    } else if (role === 'team_leader') {
        permissions['dashboard'] = { enabled: true, level: 'view', subPages: { main_dashboard: 'view', qc_dashboard: 'none' } };
        permissions['sales'] = { enabled: true, level: 'edit', subPages: { 
            sales_crm: 'edit', sales_dashboard: 'view', today_followups: 'edit', leads_pool: 'view', approvals: 'view' 
        }};
    } else if (role === 'sales_executive') {
        permissions['dashboard'] = { enabled: true, level: 'view', subPages: { main_dashboard: 'view', qc_dashboard: 'none' } };
        permissions['sales'] = { enabled: true, level: 'edit', subPages: { 
            sales_crm: 'edit', sales_dashboard: 'view', today_followups: 'edit', leads_pool: 'none', approvals: 'none' 
        }};
    } else if (role === 'cs_head') {
        permissions['dashboard'] = { enabled: true, level: 'view', subPages: { main_dashboard: 'view', qc_dashboard: 'view' } };
        permissions['customer_service'] = { enabled: true, level: 'full', subPages: { 
            cs_kanban: 'full', cs_dashboard: 'full', customer_master: 'full' 
        }};
    } else if (role === 'cs_agent') {
        permissions['dashboard'] = { enabled: true, level: 'view', subPages: { main_dashboard: 'view', qc_dashboard: 'none' } };
        permissions['customer_service'] = { enabled: true, level: 'edit', subPages: { 
            cs_kanban: 'edit', cs_dashboard: 'view', customer_master: 'view' 
        }};
    } else if (role === 'mentor' || role === 'academic_master') {
        permissions['dashboard'] = { enabled: true, level: 'view', subPages: { main_dashboard: 'view', qc_dashboard: 'none' } };
        permissions['mentor'] = { enabled: true, level: role === 'academic_master' ? 'full' : 'edit', subPages: { 
            mentor_crm: role === 'academic_master' ? 'full' : 'edit', 
            mentor_dashboard: 'view', 
            leaderboard: 'view' 
        }};
    } else if (role === 'hr') {
        permissions['dashboard'] = { enabled: true, level: 'view', subPages: { main_dashboard: 'view', qc_dashboard: 'none' } };
        permissions['hr'] = { enabled: true, level: 'full', subPages: {
            hr_dashboard: 'full', employee_master: 'full', leave_management: 'full',
            attendance: 'full', biocloud_sync: 'full', payroll: 'edit',
            performance: 'full', hr_assets: 'full', hr_analytics: 'view',
            wfh_verification: 'full', approval_pipeline: 'full',
            department_performance: 'view', offboarding: 'full',
            payroll_pre_run: 'edit', timesheet_team: 'view',
            hr_approvals: 'full', leave_approvals: 'full', regularization_approvals: 'full',
            shift_master: 'full', shift_assignment: 'full', late_rules: 'full',
        }};
        permissions['settings'] = { enabled: true, level: 'view', subPages: { 
            user_management: 'view', departments: 'edit', courses: 'none',
            access_control: 'none', role_management: 'none', teams_management: 'none',
            password_resets: 'none', audit_log: 'none', admin_settings: 'none'
        }};
    } else if (role === 'finance') {
        permissions['dashboard'] = { enabled: true, level: 'view', subPages: { main_dashboard: 'view', qc_dashboard: 'none' } };
        permissions['finance'] = { enabled: true, level: 'full', subPages: { 
            finance_dashboard: 'full', commission_engine: 'edit', payments: 'full', reconciliation: 'full' 
        }};
        permissions['customer_service'] = { enabled: true, level: 'view', subPages: { 
            cs_kanban: 'none', cs_dashboard: 'none', customer_master: 'view' 
        }};
    }
    
    return permissions;
};

function ModuleCard({ module, permissions, onPermissionChange, onModuleToggle, isExpanded, onToggleExpand }) {
    const Icon = module.icon;
    const modulePerms = permissions[module.id] || { enabled: false, level: 'none', subPages: {} };
    const isEnabled = modulePerms.enabled;
    
    // Calculate how many subpages have access
    const accessCount = Object.values(modulePerms.subPages || {}).filter(p => p !== 'none').length;
    const totalSubPages = module.subPages.length;
    
    return (
        <Card className={`transition-all ${isEnabled ? 'border-primary/50' : 'border-muted opacity-70'}`}>
            <Collapsible open={isExpanded} onOpenChange={onToggleExpand}>
                <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors py-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                {isExpanded ? (
                                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                ) : (
                                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                )}
                                <div className={`p-2 rounded-lg ${isEnabled ? 'bg-primary/10' : 'bg-muted'}`}>
                                    <Icon className={`h-5 w-5 ${isEnabled ? 'text-primary' : 'text-muted-foreground'}`} />
                                </div>
                                <div>
                                    <CardTitle className="text-base">{module.label}</CardTitle>
                                    <CardDescription className="text-xs">
                                        {accessCount} of {totalSubPages} pages accessible
                                    </CardDescription>
                                </div>
                            </div>
                            <div className="flex items-center gap-4" onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center gap-2">
                                    <Label htmlFor={`module-${module.id}`} className="text-sm">
                                        {isEnabled ? 'Enabled' : 'Disabled'}
                                    </Label>
                                    <Switch
                                        id={`module-${module.id}`}
                                        checked={isEnabled}
                                        onCheckedChange={(checked) => onModuleToggle(module.id, checked)}
                                    />
                                </div>
                            </div>
                        </div>
                    </CardHeader>
                </CollapsibleTrigger>
                
                <CollapsibleContent>
                    <CardContent className="pt-0 pb-4">
                        <div className="space-y-2">
                            {/* Module-level permission */}
                            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg mb-3">
                                <span className="text-sm font-medium">Default Module Access</span>
                                <div className="flex gap-1">
                                    {PERMISSION_LEVELS.map((level) => (
                                        <Button
                                            key={level.id}
                                            size="sm"
                                            variant={modulePerms.level === level.id ? 'default' : 'outline'}
                                            className={`h-7 px-2 text-xs ${modulePerms.level === level.id ? level.color : ''}`}
                                            onClick={() => onPermissionChange(module.id, null, level.id)}
                                            disabled={!isEnabled}
                                        >
                                            {level.label}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                            
                            {/* Sub-page permissions */}
                            <div className="text-xs font-medium text-muted-foreground mb-2 px-1">
                                Individual Page Permissions:
                            </div>
                            {module.subPages.map((subPage) => {
                                const subPagePerm = modulePerms.subPages?.[subPage.id] || 'none';
                                return (
                                    <div 
                                        key={subPage.id} 
                                        className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/20 transition-colors"
                                    >
                                        <div className="flex items-center gap-2">
                                            <div className={`w-2 h-2 rounded-full ${
                                                subPagePerm === 'none' ? 'bg-gray-300' :
                                                subPagePerm === 'view' ? 'bg-blue-500' :
                                                subPagePerm === 'edit' ? 'bg-amber-500' :
                                                'bg-emerald-500'
                                            }`} />
                                            <span className="text-sm">{subPage.label}</span>
                                            <span className="text-xs text-muted-foreground">({subPage.path})</span>
                                        </div>
                                        <div className="flex gap-1">
                                            {PERMISSION_LEVELS.map((level) => (
                                                <Button
                                                    key={level.id}
                                                    size="sm"
                                                    variant={subPagePerm === level.id ? 'default' : 'ghost'}
                                                    className={`h-6 px-2 text-xs ${
                                                        subPagePerm === level.id 
                                                            ? level.color 
                                                            : 'text-muted-foreground hover:text-foreground'
                                                    }`}
                                                    onClick={() => onPermissionChange(module.id, subPage.id, level.id)}
                                                    disabled={!isEnabled}
                                                    data-testid={`perm-${module.id}-${subPage.id}-${level.id}`}
                                                >
                                                    {level.label}
                                                </Button>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </CollapsibleContent>
            </Collapsible>
        </Card>
    );
}

function AccessControlPage() {
    const { user } = useAuth();
    const [roles, setRoles] = useState(STATIC_ROLES);
    const [selectedRole, setSelectedRole] = useState('sales_executive');
    const [permissions, setPermissions] = useState({});
    const [expandedModules, setExpandedModules] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    // Load roles from backend (includes custom roles from Role Management)
    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const res = await apiClient.get('/roles');
                const allRoles = res.data
                    .filter(r => r.id !== 'super_admin')
                    .map(r => ({ id: r.id || r.name, label: r.display_name || r.name }));
                if (allRoles.length > 0) setRoles(allRoles);
            } catch {
                // Keep static fallback
            }
        };
        fetchRoles();
    }, []);

    useEffect(() => {
        loadRolePermissions(selectedRole);
    }, [selectedRole]);

    const loadRolePermissions = async (role) => {
        setLoading(true);
        try {
            const res = await apiClient.get(`/roles/${role}/permissions`);
            if (res.data && Object.keys(res.data).length > 0) {
                setPermissions(res.data);
            } else {
                // Use defaults if no saved permissions
                setPermissions(getDefaultPermissions(role));
            }
        } catch (error) {
            // Use defaults on error
            setPermissions(getDefaultPermissions(role));
        } finally {
            setLoading(false);
            setHasChanges(false);
        }
    };

    const handleModuleToggle = (moduleId, enabled) => {
        setPermissions(prev => {
            const module = MODULE_HIERARCHY.find(m => m.id === moduleId);
            const newSubPages = {};
            module.subPages.forEach(sp => {
                newSubPages[sp.id] = enabled ? 'view' : 'none';
            });
            
            return {
                ...prev,
                [moduleId]: {
                    ...prev[moduleId],
                    enabled,
                    level: enabled ? 'view' : 'none',
                    subPages: newSubPages
                }
            };
        });
        setHasChanges(true);
    };

    const handlePermissionChange = (moduleId, subPageId, level) => {
        setPermissions(prev => {
            if (subPageId) {
                // Sub-page permission change
                return {
                    ...prev,
                    [moduleId]: {
                        ...prev[moduleId],
                        subPages: {
                            ...prev[moduleId]?.subPages,
                            [subPageId]: level
                        }
                    }
                };
            } else {
                // Module-level permission change - apply to all subpages
                const module = MODULE_HIERARCHY.find(m => m.id === moduleId);
                const newSubPages = {};
                module.subPages.forEach(sp => {
                    newSubPages[sp.id] = level;
                });
                
                return {
                    ...prev,
                    [moduleId]: {
                        ...prev[moduleId],
                        level,
                        subPages: newSubPages
                    }
                };
            }
        });
        setHasChanges(true);
    };

    const handleToggleExpand = (moduleId) => {
        setExpandedModules(prev => ({
            ...prev,
            [moduleId]: !prev[moduleId]
        }));
    };

    const expandAll = () => {
        const expanded = {};
        MODULE_HIERARCHY.forEach(m => expanded[m.id] = true);
        setExpandedModules(expanded);
    };

    const collapseAll = () => {
        setExpandedModules({});
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await apiClient.put(`/roles/${selectedRole}/permissions`, permissions);
            toast.success(`Permissions saved for ${roles.find(r => r.id === selectedRole)?.label || selectedRole}`);
            setHasChanges(false);
        } catch (error) {
            toast.error('Failed to save permissions');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        setPermissions(getDefaultPermissions(selectedRole));
        setHasChanges(true);
        toast.info('Reset to default permissions');
    };

    if (user?.role !== 'super_admin') {
        return (
            <div className="flex items-center justify-center h-64">
                <Card className="p-6 text-center">
                    <Shield className="h-12 w-12 text-red-500 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold">Access Denied</h2>
                    <p className="text-muted-foreground">Only Super Admin can access this page</p>
                </Card>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="access-control-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
                        <Shield className="h-8 w-8 text-primary" />
                        Access Control
                    </h1>
                    <p className="text-muted-foreground">
                        Configure granular permissions for each role at module and page level
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={handleReset}>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Reset to Default
                    </Button>
                    <Button 
                        onClick={handleSave} 
                        disabled={saving || !hasChanges}
                        className={hasChanges ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                    >
                        <Save className="h-4 w-4 mr-2" />
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </div>
            </div>

            {/* Role Selector */}
            <Card>
                <CardContent className="py-4">
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                        <div className="flex items-center gap-2">
                            <Label>Configure permissions for:</Label>
                            <Select value={selectedRole} onValueChange={setSelectedRole}>
                                <SelectTrigger className="w-[200px]" data-testid="role-selector">
                                    <SelectValue placeholder="Select role" />
                                </SelectTrigger>
                                <SelectContent>
                                    {roles.map((role) => (
                                        <SelectItem key={role.id} value={role.id}>
                                            {role.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex-1" />
                        <div className="flex items-center gap-2">
                            <Button variant="ghost" size="sm" onClick={expandAll}>
                                Expand All
                            </Button>
                            <Button variant="ghost" size="sm" onClick={collapseAll}>
                                Collapse All
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Permission Legend */}
            <div className="flex items-center gap-4 text-sm">
                <span className="text-muted-foreground">Permission Levels:</span>
                {PERMISSION_LEVELS.map((level) => (
                    <div key={level.id} className="flex items-center gap-1">
                        <div className={`px-2 py-0.5 rounded text-xs ${level.color}`}>
                            {level.label}
                        </div>
                    </div>
                ))}
            </div>

            {/* Module Cards */}
            {loading ? (
                <div className="flex items-center justify-center h-64">
                    <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                </div>
            ) : (
                <div className="grid gap-4">
                    {MODULE_HIERARCHY.map((module) => (
                        <ModuleCard
                            key={module.id}
                            module={module}
                            permissions={permissions}
                            onPermissionChange={handlePermissionChange}
                            onModuleToggle={handleModuleToggle}
                            isExpanded={expandedModules[module.id]}
                            onToggleExpand={() => handleToggleExpand(module.id)}
                        />
                    ))}
                </div>
            )}

            {/* Sticky Save Button for Mobile */}
            {hasChanges && (
                <div className="fixed bottom-4 right-4 md:hidden">
                    <Button 
                        onClick={handleSave} 
                        disabled={saving}
                        size="lg"
                        className="bg-emerald-600 hover:bg-emerald-700 shadow-lg"
                    >
                        <Save className="h-4 w-4 mr-2" />
                        Save
                    </Button>
                </div>
            )}
        </div>
    );
}

export default AccessControlPage;
